import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PackagePlus } from "lucide-react";
import { toast } from "sonner";
import { api, UNITS } from "@/lib/api";
import { parseNum } from "@/lib/format";
import { Button, Field, Input, Switch } from "@/components/kit";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SelectCreate } from "@/components/SelectCreate";

const EMPTY = { name: "", category_id: "", sector_id: "none", unit: "kg", unit_cost: "", sales_code: "", active: true };

function Pick({ label, value, onChange, items, placeholder, testid }) {
  return (
    <Field label={label}>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="input h-auto" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent className="bg-[var(--surface)] text-[var(--text)] border-[var(--border)] z-[60]">
          {items.map((i) => <SelectItem key={i.id} value={i.id} data-testid={`${testid}-${i.id}`}>{i.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </Field>
  );
}

export function QuickProductDialog({ open, onOpenChange, onCreated }) {
  const qc = useQueryClient();
  const [f, setF] = useState(EMPTY);
  const [err, setErr] = useState("");
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const close = () => { setF(EMPTY); setErr(""); onOpenChange(false); };
  const save = useMutation({
    mutationFn: () => api("/products", { method: "POST", body: { ...f, name: f.name.trim(), sector_id: f.sector_id === "none" ? null : f.sector_id, unit_cost: parseNum(f.unit_cost) } }),
    onSuccess: async (p) => {
      await qc.invalidateQueries({ queryKey: ["products"] });
      onCreated(p);
      toast.success("Produto cadastrado e adicionado à ficha");
      close();
    },
    onError: (e) => setErr(e.message),
  });
  const submit = () => {
    setErr("");
    if (!f.name.trim()) return setErr("Informe o nome do produto");
    if (!f.category_id) return setErr("Selecione uma categoria");
    save.mutate();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent onEscapeKeyDown={(e) => { if (document.activeElement?.dataset?.testid?.endsWith("-new-input")) e.preventDefault(); }} className="bg-[var(--surface)] text-[var(--text)] border-[var(--border)] rounded-2xl max-w-lg" data-testid="quick-product-dialog">
        <DialogTitle className="h2 flex items-center gap-2"><PackagePlus size={18} style={{ color: "var(--brand)" }} /> Novo produto / insumo</DialogTitle>
        <div className="flex flex-col gap-4">
          <Field label="Nome"><Input autoFocus placeholder="Ex.: Filé mignon" value={f.name} onChange={(e) => set("name")(e.target.value)} data-testid="quick-product-name-input" /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <SelectCreate label="Categoria" endpoint="/categories" value={f.category_id} onChange={set("category_id")} enabled={open} testid="quick-product-category" />
            <SelectCreate label="Setor de contagem" endpoint="/sectors" value={f.sector_id === "none" ? "" : f.sector_id} onChange={(v) => set("sector_id")(v || "none")} allowNone noneLabel="Sem setor" newLabel="Novo setor" createdMsg="Setor criado" enabled={open} testid="quick-product-sector" />
            <Pick label="Unidade de medida" value={f.unit} onChange={set("unit")} items={UNITS.map((u) => ({ id: u, name: u }))} testid="quick-product-unit" />
            <Field label={`Custo por ${f.unit} (R$)`}><Input inputMode="decimal" placeholder="0,00" value={f.unit_cost} onChange={(e) => set("unit_cost")(e.target.value)} data-testid="quick-product-cost-input" /></Field>
          </div>
          <Field label="Código no caixa / PDV (opcional)"><Input placeholder="Ex.: 2045" value={f.sales_code} onChange={(e) => set("sales_code")(e.target.value)} data-testid="quick-product-sales-code-input" /></Field>
          <div className="flex items-center gap-4">
            <div className="flex-1"><div className="label">Produto ativo</div><div className="caption">Entra nas novas conferências.</div></div>
            <Switch on={f.active} onChange={set("active")} data-testid="quick-product-active-switch" />
          </div>
          {err && <span className="caption" style={{ color: "var(--danger)" }} data-testid="quick-product-error">{err}</span>}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={close} data-testid="quick-product-cancel">Cancelar</Button>
            <Button loading={save.isPending} onClick={submit} data-testid="quick-product-save">Salvar e adicionar</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
